import { Avatar, useTheme, IconButton } from '@mui/material';
import Stack from '@mui/material/Stack';
import { isBefore } from 'date-fns';
import { isEmpty } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { fetchNonProfitById } from '../../stores/nonProfitActions';
import useStore from '../../stores/store';
import { canDonateToNonProfit } from '../../stores/userActions';
import { getAvatarText } from '../../utils/stringHelpers';
import FollowNonProfitButton from '../Buttons/FollowNonProfitButton';
import ClickableTooltip from '../Styled/ClickableTooltip';
import StyledBoldTypography from '../Styled/StyledBoldTypography';
import StyledContainedButton from '../Styled/StyledContainedButton';
import VolunteeringOpportunityModel from '../Volunteering/volunteeringOpportunityModel';

function NonProfitBar(props) {
  const {
    nonProfitId,
    userVolunteeringStatus = 'none',
    onLogEffort,
    onSubmit,
    opportunity = null,
    npo,
  } = props;
  const { isNonProfitAdmin } = useStore((state) => state.user);
  const theme = useTheme();
  const history = useRouter();
  const [nonProfit, setNonProfit] = React.useState(npo);
  const [donationStatus, setDonationStatus] = React.useState({
    canDonate: true,
  });

  useEffect(() => {
    const fetchNonProfit = async () => {
      const np = await fetchNonProfitById(nonProfitId);
      if (np) {
        setNonProfit(np);
        setDonationStatus(canDonateToNonProfit(np));
      } else {
        setNonProfit(null);
        setDonationStatus({ canDonate: false });
      }
    };
    if (!nonProfit || nonProfitId !== nonProfit.id) {
      fetchNonProfit();
    }
  }, [nonProfitId]);

  const hasEnded = isEmpty(opportunity.applyDeadline)
    ? isBefore(new Date(opportunity.endsOn), new Date())
    : isBefore(new Date(opportunity.applyDeadline), new Date());

  const infoMessage =
    userVolunteeringStatus === 'completed' || hasEnded
      ? 'The volunteering opportunity is closed'
      : null;

  if (!nonProfit) {
    return null;
  }

  const slug = nonProfit.name.toLowerCase().replace(/\s+/g, '-');

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%">
      <Stack
        direction="row"
        alignItems="center"
        role="button"
        onClick={() => history.push(`/nonprofit/${slug}-${nonProfit.id}`)}>
        <Avatar
          src={nonProfit.logo_url}
          alt={nonProfit.name}
          size="large"
          style={{
            backgroundColor: theme.palette.text.white,
            marginRight: 4,
            border: `1px solid ${theme.palette.secondary.main}`,
            width: 60,
            height: 60,
          }}
          imgProps={{
            style: {
              objectFit: 'scale-down',
            },
          }}>
          <Stack justifyContent="center" alignItems="center" height={140}>
            <StyledBoldTypography variant="h6" color="primary">
              {getAvatarText(nonProfit?.name)}
            </StyledBoldTypography>
          </Stack>
        </Avatar>
        <StyledBoldTypography variant="subtitle1">
          {nonProfit.name}
        </StyledBoldTypography>
        <IconButton className="ml-1">
          <FollowNonProfitButton nonProfit={nonProfit} />
        </IconButton>
      </Stack>
      <Stack
        sx={{
          flexDirection: { sm: 'column', md: 'row' },
          alignItems: { sm: 'start', md: 'center' },
        }}
        gap={1}>
        {!isNonProfitAdmin && !!infoMessage && (
          <ClickableTooltip message={infoMessage} />
        )}

        {!isNonProfitAdmin &&
          (userVolunteeringStatus === 'active' ? (
            <StyledContainedButton
              variant="body2"
              style={{ padding: '4px 12px' }}
              onClick={onLogEffort}>
              Log Effort
            </StyledContainedButton>
          ) : (
            <StyledContainedButton
              variant="body2"
              style={{ padding: '4px 12px' }}
              onClick={onSubmit}
              disabled={
                userVolunteeringStatus !== 'none' ||
                userVolunteeringStatus === 'pending' ||
                opportunity.totalVolunteers >= opportunity.volunteersReq ||
                hasEnded
              }>
              {userVolunteeringStatus === 'pending'
                ? 'Pending Review'
                : 'Volunteer Now'}
            </StyledContainedButton>
          ))}
        {/* <FollowNonProfitButton nonProfit={nonProfit} /> */}
      </Stack>
    </Stack>
  );
}

export default NonProfitBar;
